import { constants, metadata, dbViews, http } from 'lib_ui-services';
import { NON_USER_FILTER_TYPES } from 'lib_ui-services/src/dbViews/viewCriteria';
import { format } from 'date-fns';
import { addTimezoneToColumns } from '../../namespace/relation/exportHelpers/utils';
import getAllFeatureFlags from '../../../../utilities/getAllFeatureFlags';
const { EXPORT_TYPES, notificationTypes } = constants;
const FF_ITEM_AUDIT_HISTORY_EXPORT = 'Item Audit History Export';
// This rule always generates the report server-side, so it must signal SERVER_SIDE_EXPORT to the
// downstream doingExport step; otherwise that step ignores the generated reportId and falls back
// to client-side export.
const FF_SERVER_SIDE_EXPORT = 'SERVER_SIDE_EXPORT';
const DISALLOWED_EXPORT_TYPES = ['pdf', 'html'];

// The Item Audit History grid renders a single "Changes" (PatchDetail) column that the client
// expands into Type / Field / Old Value / New Value via the patchDetail filter. The server-side
// export has no client filter, so we expand that one column into the same four columns here.
// Their propertyNames match the flat keys emitted per row by the item:item-patch report module
// (fieldOperation, fieldLabel, oldValue, newValue), so the CSV/XLSX mirrors the grid exactly.
const PATCH_DETAIL_SUBCOLUMNS = [
    { propertyName: 'fieldOperation', label: 'Type' },
    { propertyName: 'fieldLabel', label: 'Field' },
    { propertyName: 'oldValue', label: 'Old Value' },
    { propertyName: 'newValue', label: 'New Value' }
];

const _p = {
    http,
    getDbView: dbViews.getDbView,
    getPrettyRelationName: metadata.getPrettyRelationName,
    addTimezoneToColumns,
    getAllFeatureFlags,
    confirmExport
};

export const _private = _p;

export default {
    verb: 'willExport',
    namespace: 'item',
    relation: 'item-patch',
    description: 'Server-side export for item audit history — fans out patches into one row per field change',
    priority: 10,
    logic: will_export_item_patch,
    onError
};

/**
 * @param {{
 *   data: object;
 *   context: import("rulesengine.io").Context;
 *   dispatch: (data:object,context:object,awaitResult?:boolean)=>Promise<any>
 * }} parameters
 */
async function will_export_item_patch({ data, context, dispatch }) {
    const { namespace, relation, type } = context;
    const featureFlags = [...new Set([..._p.getAllFeatureFlags(context), FF_SERVER_SIDE_EXPORT])];

    // When aborting, the rules engine short-circuits without dispatching a status event.
    // Manually dispatch status:'failure' so that useExportingState (and showBusyUntilFinished
    // buttons) reset correctly and the export button is re-enabled.
    async function abort() {
        await dispatch(
            { errors: { field: {}, form: [] } },
            { ...context, status: 'failure' }
        );
        return { error: { field: {}, aborted: true }, data: {} };
    }

    if (!featureFlags.includes(FF_ITEM_AUDIT_HISTORY_EXPORT)) {
        await sendNotification(
            'Item Audit History export requires the Item Audit History Export feature to be enabled.',
            dispatch
        );
        return abort();
    }
    if (!data.isConnected) {
        await sendNotification('You must be online to export Item Audit History.', dispatch);
        return abort();
    }
    if (DISALLOWED_EXPORT_TYPES.includes(type)) {
        await sendNotification(
            `${type.toUpperCase()} format is not supported for Item Audit History export. Please select CSV or XLSX.`,
            dispatch
        );
        return abort();
    }

    const relationLabel = _p.getPrettyRelationName(namespace, relation) || data.filePrefix || `export_${relation}`;
    const filePrefix = data.filePrefix || relationLabel;
    const timestamp = format(new Date(), "yyyyMMMdd'T'hhmmssaaa");
    const fileName = `${filePrefix}_${timestamp}`;
    const columnsWithTimezone = _p.addTimezoneToColumns(data.columns);
    const columns = expandPatchDetailColumns(columnsWithTimezone);

    const exportFields = {
        title: fileName,
        fileName,
        filePrefix,
        navigationSelection: data.navigationSelection,
        featureFlags,
        namespace,
        relation,
        relationLabel,
        fileType: EXPORT_TYPES[type] || EXPORT_TYPES.csv,
        columns,
        reportType: 'item_item-patch'
    };

    const countResponse = await dispatch(
        { criteria: { 'meta.deleted': { $exists: false } } },
        { verb: 'count', namespace, relation },
        true
    );
    const count = countResponse?.result ? countResponse.result[0] : 0;
    if (!count) {
        await sendNotification(`No ${relationLabel} to Export`, dispatch);
        return abort();
    }

    const view = await _p.getDbView(namespace, relation, data.viewName);
    const filters = view.viewCriteria.getFiltersFromView();

    // Detect when the current filter yields no records.
    // For non-limitSyncSize relations: view.count is the filtered local count.
    // For limitSyncSize relations: gateResultIds is reset to [] on every user-driven
    // filter change (onBeforeChange) and is only populated when the server returns results.
    // If it is still [] after a completed fetch (page >= 1, awaitingFirstServerPage false),
    // the server returned 0 records for the current filter.
    // This gate mechanism only applies for non-FTS find filters; skip it for full-text search.
    // Share the exclusion set with dbViews rather than restating it. A local copy here missed
    // `propertiesToSearch` — metadata-only, no getMql, and present on every grid that has a
    // search box even with an empty term — so findFilters was never empty and this whole
    // condition rode on the remaining conjuncts (#1703).
    const findFilters = Object.keys(filters).filter(f => !NON_USER_FILTER_TYPES.has(f));
    const serverReturnedNoResults =
        !!data.limitSyncSize &&
        findFilters.length > 0 &&
        filters.fullTextSearch == null &&
        !view.awaitingFirstServerPage &&
        view.page >= 1 &&
        !view.gateResultIds?.length;

    if (view.count === 0 || serverReturnedNoResults) {
        await sendNotification(`No ${relationLabel} to Export`, dispatch);
        return abort();
    }

    const confirmedExport = await _p.confirmExport({ relationLabel, dispatch });
    if (!confirmedExport) {
        return abort();
    }

    const urlFilters = { ...filters };
    delete urlFilters.caseSensitive;
    delete urlFilters.alsoSearchWithAscii;
    const url = _p.http.getQueryUrl('report', 'generate', urlFilters, undefined, { includeEndpointSuffix: false });
    const response = await _p.http.post(url, exportFields, true);

    if (response && response._id) {
        await sendNotification(`Preparing to export ${relationLabel} records`, dispatch);
        exportFields.reportId = response._id;
    } else {
        return abort();
    }

    return { data: { ...data, ...exportFields }, context };
}

/**
 * Replace any PatchDetail "Changes" column with the four columns shown in the grid
 * (Type, Field, Old Value, New Value), preserving overall column order via sequence.
 * Non-PatchDetail columns are passed through unchanged.
 * @param {Object[]} columns
 * @returns {Object[]}
 */
function expandPatchDetailColumns(columns = []) {
    const sorted = [...columns].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    let sequenceOffset = 0;
    return sorted.flatMap(column => {
        const isPatchDetail = column.dataType === 'patchDetail' || column.hNodeType === 'PatchDetail';
        if (!isPatchDetail) {
            return [{ ...column, sequence: (column.sequence || 0) + sequenceOffset }];
        }
        const baseSequence = (column.sequence || 0) + sequenceOffset;
        sequenceOffset += PATCH_DETAIL_SUBCOLUMNS.length - 1;
        return PATCH_DETAIL_SUBCOLUMNS.map((sub, index) => ({
            ...column,
            propertyName: sub.propertyName,
            label: sub.label,
            title: sub.label,
            dataType: 'text',
            hNodeType: 'ShortText',
            sequence: baseSequence + index
        }));
    });
}

async function sendNotification(message, dispatch) {
    await dispatch(
        {
            addToList: false,
            message,
            variant: 'warning',
            type: notificationTypes.TOAST
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );
}

function onError({ error, dispatch }) {
    dispatch(
        {
            message: `Export failed: ${error.message}`,
            variant: 'error',
            type: notificationTypes.TOAST
        },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );
    throw error;
}

function confirmExport({ relationLabel, dispatch }) {
    return new Promise(resolve => {
        const message = [
            `This action will export your ${relationLabel} records, and could take some time to complete.`,
            'Refreshing or closing this page will interrupt the export, but you can continue to navigate within the application.',
            'Continue?'
        ];
        dispatch(
            {
                message,
                okButtonText: 'YES',
                icon: 'warning',
                iconColorStyle: constants.COLOR_STYLES.Warning,
                variant: constants.VARIANT.Warning,
                okAction: () => resolve(true),
                cancelAction: () => resolve(false)
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        );
    });
}
